import "./views.scss";

import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";

import Alert from "../components/alert/alert";
import { AlertMessageDefault } from "../components/messages/AlertMessageDefault";
import { ContentMessageDefault } from "../components/messages/ContentMessageDefault";
import { triggerErrorView } from "../helpers/observability/analytics";

/**
 * @description ErrorInfoComponent is a reusable component to display error information with customizable messages, alert, and a "Go back" button.
 * @param {object} props - Component props.
 * @param {React.ReactNode} [props.title=<>Something went wrong.</>] - The title of the error page.
 * @param {React.ComponentType} [props.AlertMessage=AlertMessageDefault] - The component to display in the alert message.
 * @param {React.ComponentType} [props.ContentMessage=ContentMessageDefault] - The component to display as the content message.
 * @param {boolean} [props.showGoBack=true] - Whether to show the "Go back" button.
 * @param {string} [props.errorType="general"] - The type of error.
 * @param {string} [props.errorCode="error"] - The error code.
 * @param {string} [props.errorMessage="Something went wrong"] - The error message.
 * @param {function} props.errorBack - The function to call when the "Go back" button is clicked.
 * @param {object} [props.errorBackData={ view: "welcome", currentPage: 1 }] - The data to pass to the errorBack function.
 * @returns {JSX.Element} - The ErrorInfoComponent JSX element.
 */
const ErrorInfoComponent = (props) => {
  const {
    title = <>Something went wrong.</>,
    AlertMessage = AlertMessageDefault,
    ContentMessage = ContentMessageDefault,
    showGoBack = true,
    errorType = "general",
    errorCode = "error",
    errorMessage = "Something went wrong",
    errorBack,
    errorBackData = { view: "welcome", currentPage: 1 }
  } = props;

  useEffect(() => {
    triggerErrorView({
      event_action: "error_view",
      error_message: errorMessage,
      error_type: errorType,
      event_label: `${errorCode}:${errorMessage}`,
      event_category: "registration_app"
    });
  }, [errorCode, errorType, errorMessage]);

  return (
    <div className="display-grid gap-base views error">
      <div className="inner">
        <div className="header p-base">
          <h3 className="serif">
            <strong>{title}</strong>
          </h3>
        </div>
        <Alert type="danger">
          <AlertMessage />
        </Alert>
        <div className="display-flex flex-col align-center">
          {showGoBack ? (
            <button
              className="btn-text-blue base navigate mb-xs align-self-start"
              onClick={() => errorBack(errorBackData)}>
              <strong>
                <FontAwesomeIcon icon={faChevronLeft} /> Go back and try again
              </strong>
            </button>
          ) : null}
          <ContentMessage />
        </div>
      </div>
    </div>
  );
};

export default ErrorInfoComponent;
